import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Spin } from 'antd'
import { groupBy } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import {
  AllergenFormListItem,
  DirtyPromptForm,
  FormButtons,
  FormListSelect,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  optionPropType,
  simpleEntityPropType,
} from '../../../common/prop-types'

import { useUpdateAllergenIncludedAllergensDetailsMutation } from '../../hooks'
import { setAllergenPageIsDirty } from '../../redux/actions'

import { allergenIncludedAllergensDetailsFormInitialValues } from './initial-values'

const AllergenIncludedAllergensDetailsForm = ({
  allergenId,
  includedAllergens,
  allergenOptions,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.ALLERGEN.SAVE_ALLERGENS,
  ])

  const [
    updateAllergenIncludedAllergensDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateAllergenIncludedAllergensDetailsMutation()

  const initialValues = useMemo(
    () => allergenIncludedAllergensDetailsFormInitialValues(includedAllergens),
    [includedAllergens],
  )

  const allergenOptionsGroupedById = useMemo(
    () => groupBy(allergenOptions, 'value'),
    [allergenOptions],
  )

  const allergenIdsRules = useMemo(
    () => [
      () => ({
        validator(_, value) {
          try {
            if (areSameValue(value, allergenId)) {
              throw new Error(`Can't select owning allergen`)
            }

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [allergenId],
  )

  const itemRender = useCallback(
    ({
      field: { key, name, fieldKey, ...restField },
      shouldModify,
      treeData,
      onClear,
      itemRules,
      ...rest
    }) => (
      <Form.Item
        key={key}
        name={name}
        fieldKey={fieldKey}
        rules={itemRules}
        noStyle
        {...restField}
      >
        <AllergenFormListItem
          shouldModify={shouldModify}
          name={name}
          treeData={treeData}
          onClear={onClear}
          allergenOptionsGroupedById={allergenOptionsGroupedById}
          {...rest}
        />
      </Form.Item>
    ),
    [allergenOptionsGroupedById],
  )

  const handleSaveData = useCallback(
    data => updateAllergenIncludedAllergensDetails(allergenId, data),
    [allergenId, updateAllergenIncludedAllergensDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Allergen details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [includedAllergens, resetForm])

  useEffect(() => {
    dispatch(setAllergenPageIsDirty({ isIncludedAllergensDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="includedAllergensForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledVerticalSpace>
        <FormListSelect
          disabled={!isFormEnabled}
          form={form}
          labelAbove
          name="allergenIncludedAllergenIds"
          label="Included Allergens"
          showCountItems
          showSearch
          options={allergenOptions}
          newItemSelectPlaceholder="Select a new allergen..."
          isDirty={isDirty}
          itemRender={itemRender}
          itemRules={allergenIdsRules}
        />
        <FormButtons
          permissions={[PERMISSIONS.ALLERGEN.SAVE_ALLERGENS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

AllergenIncludedAllergensDetailsForm.propTypes = {
  allergenId: idPropType.isRequired,
  includedAllergens: PropTypes.arrayOf(simpleEntityPropType),
  allergenOptions: PropTypes.arrayOf(optionPropType.isRequired).isRequired,
}

AllergenIncludedAllergensDetailsForm.defaultProps = {
  includedAllergens: [],
}

export default AllergenIncludedAllergensDetailsForm
